import { createContext, useContext, useReducer, useEffect } from 'react'
import type { ReactNode } from 'react'
import type { Basket, Food, Nutrient, NutrientIndicator, ScoreResult, CustomNutrientConstraints, CustomPortions, CustomPurchaseInfo, UserPreferences, UserProfile, EstimatedTargets } from '../types'
import { DEFAULT_USER_PROFILE } from '../types'
import { nutritionData } from '../../generated/data'
import {
  evaluateBasket,
  estimateAll,
  addPortion,
  removePortion as removePortionOp,
  removeFood as removeFoodOp,
  clearBasket as clearBasketOp,
  setFoodGrams as setFoodGramsOp,
  serializeBasket,
  deserializeBasket
} from '../scoring'

// Action types
type BasketAction =
  | { type: 'ADD_PORTION'; foodId: string }
  | { type: 'REMOVE_PORTION'; foodId: string }
  | { type: 'REMOVE_FOOD'; foodId: string }
  | { type: 'CLEAR_BASKET' }
  | { type: 'LOAD_BASKET'; basket: Basket }
  | { type: 'SET_GRAMS'; foodId: string; grams: number }
  | { type: 'UPDATE_NUTRIENT_CONSTRAINT'; nutrientId: number; constraint: Partial<CustomNutrientConstraints[number]> }
  | { type: 'RESET_NUTRIENT_CONSTRAINT'; nutrientId: number }
  | { type: 'UPDATE_CUSTOM_PORTION'; foodId: string; grams: number }
  | { type: 'RESET_CUSTOM_PORTION'; foodId: string }
  | { type: 'UPDATE_CUSTOM_PURCHASE_INFO'; foodId: string; info: { purchaseRawGrams: number, purchasePrice: number } }
  | { type: 'RESET_CUSTOM_PURCHASE_INFO'; foodId: string }
  | { type: 'LOAD_PREFERENCES'; preferences: UserPreferences }
  | { type: 'SET_SELECTED_CATEGORIES'; categories: Set<string> }
  | { type: 'SET_HIDE_IGNORED'; hide: boolean }
  | { type: 'MOVE_ITEM_UP'; foodId: string }
  | { type: 'MOVE_ITEM_DOWN'; foodId: string }
  | { type: 'SET_USER_PROFILE'; profile: UserProfile }

// State type
interface BasketState {
  basket: Basket
  basketOrder: string[] // food IDs in display order
  scoreResult: ScoreResult
  foods: Food[]
  nutrients: Nutrient[]
  userProfile: UserProfile
  estimatedConstraints: Record<number, EstimatedTargets>
  customConstraints: CustomNutrientConstraints
  customPortions: CustomPortions
  customPurchaseInfo: CustomPurchaseInfo
  selectedNutrientCategories: Set<string>
  hideIgnoredNutrients: boolean
  isLoading: boolean
}

// Context type
interface BasketContextType extends BasketState {
  addFood: (foodId: string) => void
  removePortion: (foodId: string) => void
  removeFood: (foodId: string) => void
  setFoodGrams: (foodId: string, grams: number) => void
  clearBasket: () => void
  updateNutrientConstraint: (nutrientId: number, constraint: Partial<CustomNutrientConstraints[number]>) => void
  resetNutrientConstraint: (nutrientId: number) => void
  updateCustomPortion: (foodId: string, grams: number) => void
  resetCustomPortion: (foodId: string) => void
  updateCustomPurchaseInfo: (foodId: string, info: { purchaseRawGrams: number, purchasePrice: number }) => void
  resetCustomPurchaseInfo: (foodId: string) => void
  setSelectedNutrientCategories: (categories: Set<string>) => void
  setHideIgnoredNutrients: (hide: boolean) => void
  moveItemUp: (foodId: string) => void
  moveItemDown: (foodId: string) => void
  setUserProfile: (profile: UserProfile) => void
  filteredIndicators: NutrientIndicator[]
  exportPreferences: () => string
  importPreferences: (data: string) => void
}

// Reducer
function basketReducer(state: BasketState, action: BasketAction): BasketState {
  let newBasket: Basket = state.basket
  let newBasketOrder: string[] = state.basketOrder
  let newUserProfile: UserProfile = state.userProfile
  let newEstimatedConstraints: Record<number, EstimatedTargets> = state.estimatedConstraints
  let newCustomConstraints: CustomNutrientConstraints = state.customConstraints
  let newCustomPortions: CustomPortions = state.customPortions
  let newCustomPurchaseInfo: CustomPurchaseInfo = state.customPurchaseInfo

  switch (action.type) {
    case 'ADD_PORTION': {
      const food = state.foods.find(f => f.id === action.foodId)
      if (food) {
        const customPortion = newCustomPortions[action.foodId]
        const effectiveFood = customPortion !== undefined
          ? { ...food, portionGrams: customPortion }
          : food
        newBasket = addPortion(state.basket, action.foodId, effectiveFood)
        if (!newBasketOrder.includes(action.foodId)) {
          newBasketOrder = [...newBasketOrder, action.foodId]
        }
      }
      break
    }
    case 'REMOVE_PORTION': {
      const food = state.foods.find(f => f.id === action.foodId)
      if (food) {
        const customPortion = newCustomPortions[action.foodId]
        const effectiveFood = customPortion !== undefined
          ? { ...food, portionGrams: customPortion }
          : food
        newBasket = removePortionOp(state.basket, action.foodId, effectiveFood)
      }
      break
    }
    case 'REMOVE_FOOD':
      newBasket = removeFoodOp(state.basket, action.foodId)
      // Remove from order
      newBasketOrder = newBasketOrder.filter(id => id !== action.foodId)
      break
    case 'CLEAR_BASKET':
      newBasket = clearBasketOp()
      newBasketOrder = []
      break
    case 'LOAD_BASKET':
      newBasket = action.basket
      // Initialize basketOrder from basket keys if not already set
      if (newBasketOrder.length === 0 && Object.keys(newBasket).length > 0) {
        newBasketOrder = Object.keys(newBasket)
      }
      break
    case 'SET_GRAMS':
      newBasket = setFoodGramsOp(state.basket, action.foodId, action.grams)
      // Add to order if not already present and grams > 0
      if (action.grams > 0 && !newBasketOrder.includes(action.foodId)) {
        newBasketOrder = [...newBasketOrder, action.foodId]
      }
      break
    case 'UPDATE_NUTRIENT_CONSTRAINT':
      newCustomConstraints = {
        ...state.customConstraints,
        [action.nutrientId]: {
          ...state.customConstraints[action.nutrientId],
          ...action.constraint
        }
      }
      break
    case 'RESET_NUTRIENT_CONSTRAINT':
      newCustomConstraints = { ...state.customConstraints }
      delete newCustomConstraints[action.nutrientId]
      break
    case 'UPDATE_CUSTOM_PORTION':
      newCustomPortions = {
        ...state.customPortions,
        [action.foodId]: action.grams
      }
      break
    case 'RESET_CUSTOM_PORTION':
      newCustomPortions = { ...state.customPortions }
      delete newCustomPortions[action.foodId]
      break
    case 'UPDATE_CUSTOM_PURCHASE_INFO':
      newCustomPurchaseInfo = {
        ...state.customPurchaseInfo,
        [action.foodId]: action.info
      }
      break
    case 'RESET_CUSTOM_PURCHASE_INFO':
      newCustomPurchaseInfo = { ...state.customPurchaseInfo }
      delete newCustomPurchaseInfo[action.foodId]
      break
    case 'LOAD_PREFERENCES':
      newBasket = action.preferences.basket
      newCustomConstraints = action.preferences.nutrientConstraints
      newCustomPortions = action.preferences.customPortions || {}
      newCustomPurchaseInfo = action.preferences.customPurchaseInfo || {}
      newBasketOrder = action.preferences.basketOrder || []
      if (action.preferences.userProfile) {
        newUserProfile = action.preferences.userProfile
        newEstimatedConstraints = estimateAll(action.preferences.userProfile)
      }
      break
    case 'SET_SELECTED_CATEGORIES':
      return {
        ...state,
        selectedNutrientCategories: action.categories
      }
    case 'SET_HIDE_IGNORED':
      return {
        ...state,
        hideIgnoredNutrients: action.hide
      }
    case 'MOVE_ITEM_UP': {
      const index = newBasketOrder.indexOf(action.foodId)
      if (index > 0) {
        newBasketOrder = [...newBasketOrder]
        const temp = newBasketOrder[index - 1]!
        newBasketOrder[index - 1] = newBasketOrder[index]!
        newBasketOrder[index] = temp
      }
      break
    }
    case 'MOVE_ITEM_DOWN': {
      const index = newBasketOrder.indexOf(action.foodId)
      if (index >= 0 && index < newBasketOrder.length - 1) {
        newBasketOrder = [...newBasketOrder]
        const temp = newBasketOrder[index + 1]!
        newBasketOrder[index + 1] = newBasketOrder[index]!
        newBasketOrder[index] = temp
      }
      break
    }
    case 'SET_USER_PROFILE':
      newUserProfile = action.profile
      newEstimatedConstraints = estimateAll(action.profile)
      break
    default:
      return state
  }

  // Recalculate score result with estimated + custom constraints, portions, and purchase info
  const scoreResult = evaluateBasket({
    basket: newBasket,
    foods: state.foods,
    nutrients: state.nutrients,
    estimatedConstraints: newEstimatedConstraints,
    customConstraints: newCustomConstraints,
    customPortions: newCustomPortions,
    customPurchaseInfo: newCustomPurchaseInfo
  })

  return {
    ...state,
    userProfile: newUserProfile,
    estimatedConstraints: newEstimatedConstraints,
    basket: newBasket,
    basketOrder: newBasketOrder,
    customConstraints: newCustomConstraints,
    customPortions: newCustomPortions,
    customPurchaseInfo: newCustomPurchaseInfo,
    scoreResult,
    isLoading: false // Always set loading to false after any action
  }
}

// Create context
const BasketContext = createContext<BasketContextType | null>(null)

// Provider component
interface BasketProviderProps {
  children: ReactNode
}

export function BasketProvider({ children }: BasketProviderProps) {
  const { foods, nutrients } = nutritionData

  // Initialize state with default user profile
  const initialEstimatedConstraints = estimateAll(DEFAULT_USER_PROFILE)

  const initialState: BasketState = {
    basket: {},
    basketOrder: [],
    scoreResult: {
      indicators: [],
      foodOptions: [],
      totals: { weight: 0, price: 0, score: 0 },
      overallScore: 0
    },
    foods,
    nutrients,
    userProfile: DEFAULT_USER_PROFILE,
    estimatedConstraints: initialEstimatedConstraints,
    customConstraints: {},
    customPortions: {},
    customPurchaseInfo: {},
    selectedNutrientCategories: new Set(),
    hideIgnoredNutrients: true,
    isLoading: true
  }

  const [state, dispatch] = useReducer(basketReducer, initialState)

  // Load basket and preferences from localStorage on mount
  useEffect(() => {
    try {
      const savedBasket = localStorage.getItem('kajChiFoodOptions')
      const basket = savedBasket ? deserializeBasket(savedBasket) : {}

      const savedPreferences = localStorage.getItem('kajChiUserPreferences')
      let customConstraints = {}
      let customPortions: CustomPortions = {}
      let customPurchaseInfo: CustomPurchaseInfo = {}
      let basketOrder: string[] = []
      let userProfile: UserProfile | undefined
      if (savedPreferences) {
        try {
          const preferences: UserPreferences = JSON.parse(atob(savedPreferences))
          customConstraints = preferences.nutrientConstraints || {}
          customPortions = preferences.customPortions || {}
          customPurchaseInfo = preferences.customPurchaseInfo || {}
          basketOrder = preferences.basketOrder || []
          userProfile = preferences.userProfile
        } catch {
          // Ignore corrupted preferences
        }
      }

      if (basketOrder.length === 0 && Object.keys(basket).length > 0) {
        basketOrder = Object.keys(basket)
      }

      // Always load preferences to apply user profile (even if just defaults)
      const preferences: UserPreferences = {
        basket,
        nutrientConstraints: customConstraints,
        customPortions,
        customPurchaseInfo,
        basketOrder,
        timestamp: new Date().toISOString()
      }
      if (userProfile) preferences.userProfile = userProfile
      dispatch({ type: 'LOAD_PREFERENCES', preferences })
    } catch {
      dispatch({ type: 'LOAD_BASKET', basket: {} })
    }
  }, [foods, nutrients])

  // Save basket to localStorage when it changes
  useEffect(() => {
    if (!state.isLoading) {
      try {
        localStorage.setItem('kajChiFoodOptions', serializeBasket(state.basket))
      } catch {
        // Ignore localStorage write failures
      }
    }
  }, [state.basket, state.isLoading])

  // Save preferences to localStorage when they change
  useEffect(() => {
    if (!state.isLoading) {
      try {
        const preferences: UserPreferences = {
          nutrientConstraints: state.customConstraints,
          customPortions: state.customPortions,
          customPurchaseInfo: state.customPurchaseInfo,
          basket: state.basket,
          basketOrder: state.basketOrder,
          userProfile: state.userProfile,
          timestamp: new Date().toISOString()
        }
        localStorage.setItem('kajChiUserPreferences', btoa(JSON.stringify(preferences)))
      } catch {
        // Ignore localStorage write failures
      }
    }
  }, [state.customConstraints, state.customPortions, state.customPurchaseInfo, state.basket, state.basketOrder, state.userProfile, state.isLoading])

  // Update loading state after initial calculation
  useEffect(() => {
    if (state.isLoading && state.scoreResult.indicators.length > 0) {
      dispatch({ type: 'LOAD_BASKET', basket: state.basket })
    }
  }, [state.scoreResult.indicators.length, state.isLoading, state.basket])

  // Action creators
  const addFood = (foodId: string) => {
    dispatch({ type: 'ADD_PORTION', foodId })
  }

  const removePortion = (foodId: string) => {
    dispatch({ type: 'REMOVE_PORTION', foodId })
  }

  const removeFood = (foodId: string) => {
    dispatch({ type: 'REMOVE_FOOD', foodId })
  }

  const setFoodGrams = (foodId: string, grams: number) => {
    dispatch({ type: 'SET_GRAMS', foodId, grams })
  }

  const clearBasketAction = () => {
    dispatch({ type: 'CLEAR_BASKET' })
  }

  const updateNutrientConstraint = (nutrientId: number, constraint: Partial<CustomNutrientConstraints[number]>) => {
    dispatch({ type: 'UPDATE_NUTRIENT_CONSTRAINT', nutrientId, constraint })
  }

  const resetNutrientConstraint = (nutrientId: number) => {
    dispatch({ type: 'RESET_NUTRIENT_CONSTRAINT', nutrientId })
  }

  const updateCustomPortion = (foodId: string, grams: number) => {
    dispatch({ type: 'UPDATE_CUSTOM_PORTION', foodId, grams })
  }

  const resetCustomPortion = (foodId: string) => {
    dispatch({ type: 'RESET_CUSTOM_PORTION', foodId })
  }

  const updateCustomPurchaseInfo = (foodId: string, info: { purchaseRawGrams: number, purchasePrice: number }) => {
    dispatch({ type: 'UPDATE_CUSTOM_PURCHASE_INFO', foodId, info })
  }

  const resetCustomPurchaseInfo = (foodId: string) => {
    dispatch({ type: 'RESET_CUSTOM_PURCHASE_INFO', foodId })
  }

  const moveItemUp = (foodId: string) => {
    dispatch({ type: 'MOVE_ITEM_UP', foodId })
  }

  const moveItemDown = (foodId: string) => {
    dispatch({ type: 'MOVE_ITEM_DOWN', foodId })
  }

  const setUserProfile = (profile: UserProfile) => {
    dispatch({ type: 'SET_USER_PROFILE', profile })
  }

  const exportPreferences = (): string => {
    const preferences: UserPreferences = {
      nutrientConstraints: state.customConstraints,
      customPortions: state.customPortions,
      customPurchaseInfo: state.customPurchaseInfo,
      basket: state.basket,
      basketOrder: state.basketOrder,
      userProfile: state.userProfile,
      timestamp: new Date().toISOString()
    }
    return btoa(JSON.stringify(preferences))
  }

  const importPreferences = (data: string) => {
    try {
      const preferences: UserPreferences = JSON.parse(atob(data))
      if (preferences.nutrientConstraints && preferences.basket) {
        dispatch({ type: 'LOAD_PREFERENCES', preferences })
      } else {
        throw new Error('Invalid preferences format')
      }
    } catch (error) {
      console.error('Failed to import preferences:', error)
      throw error
    }
  }

  const setSelectedNutrientCategories = (categories: Set<string>) => {
    dispatch({ type: 'SET_SELECTED_CATEGORIES', categories })
  }

  const setHideIgnoredNutrients = (hide: boolean) => {
    dispatch({ type: 'SET_HIDE_IGNORED', hide })
  }

  // Calculate filtered indicators
  const filteredIndicators = state.scoreResult.indicators.filter(indicator =>
    (state.selectedNutrientCategories.size > 0 && state.selectedNutrientCategories.has(indicator.category)) &&
    (!state.hideIgnoredNutrients || indicator.algorithm !== 'ignore')
  )

  const contextValue: BasketContextType = {
    ...state,
    addFood,
    removePortion,
    removeFood,
    setFoodGrams,
    clearBasket: clearBasketAction,
    updateNutrientConstraint,
    resetNutrientConstraint,
    updateCustomPortion,
    resetCustomPortion,
    updateCustomPurchaseInfo,
    resetCustomPurchaseInfo,
    setSelectedNutrientCategories,
    setHideIgnoredNutrients,
    moveItemUp,
    moveItemDown,
    setUserProfile,
    filteredIndicators,
    exportPreferences,
    importPreferences
  }

  return (
    <BasketContext.Provider value={contextValue}>
      {children}
    </BasketContext.Provider>
  )
}

// Hook to use the context
export function useBasket() {
  const context = useContext(BasketContext)
  if (!context) {
    throw new Error('useBasket must be used within a BasketProvider')
  }
  return context
}